import * as React from 'react';
import BPromise from 'bluebird';
import propz from 'propz';
import moment from 'moment';
import { AppUser } from 'Src/views/App/App';
import { parse } from 'query-string';
import { getOrder, getSearchFilter, getSearchOrder, getServerFieldSectionWhere } from 'Src/helpers/table/table';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE, LIMIT } from 'Src/consts/table';
import { Loader } from 'Src/components/Loader/Loader';
import { SelectAllCheckbox } from 'Src/components/SelectAllCheckbox/SelectAllCheckbox';
import {
  ColumnDefinition,
  getFilters2,
  getPagesCount,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import { History, Location } from 'history';
import {
  getClubMessageStatus,
  getUpdatedAt,
  getClubMessageDeadline,
  getIsParticipant,
  getClubDays,
  getClubStartTimeFromMessage
} from 'Src/helpers/accessor/accessor';
import { Club } from 'Src/models/club';
import {
  getClub,
  updateClubBookingType,
  getAvailableClubSpacesMax,
  cancelClubMessageOutdatedStatus
} from 'Src/helpers/service/admin/clubs';
import { getAllClubMessages, getClubMessages, getClubMessagesCount } from 'Src/helpers/service/admin/clubMessages';
import { updateClubMessage } from 'Src/helpers/service/admin/clubStudents';
import { ClubMessage } from 'Src/models/clubMessage';
import { UK_INPUT_MASK_VALIDATION, UK_INPUT_MASK } from 'Src/consts/date';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { Switch } from 'Src/components/Switch/Switch';
import { CLUB_CONFIRMATION_REQUEST_TABS, CLUB_PRICING, CLUB_STATUS } from 'Src/consts/club';
import { addClubStudents } from 'Src/helpers/service/admin/clubStudents';
import {
  getSelectOptionsForClubPriority,
  getSelectOptionsForClubMessagesStatus,
  getSelectOptionForBooleanQuestion
} from 'Src/helpers/table/select';
import { CONFIRMATION_REQUESTS_STATUS } from 'Src/consts/message';
import { GenericSummary } from 'Src/components/GenericSummary/GenericSummary';
import MaskedInput from 'react-text-mask';
import { ClubConfirmationRequestSummary } from './ClubConfirmationRequestSummary/ClubConfirmationRequestSummary';
import { DEFAULT_CLUB_TABLE_FILTERS } from './Clubs';
import { MESSAGE_INVITATION_STATUS, CLUB_PARTICIPATION_MESSAGE_INVITATION_STATUS } from 'Src/consts/message';
import {
  getRespondedAtText,
  getResponseUserText,
  statusChangedAfterFirstResponse,
  wasEditedAfterResponse
} from 'Src/helpers/message/message';
import './Clubs.css';
import '../Invitations/Invitations.css';
import { loadHiddenColumns, saveHiddenColumns } from 'Src/helpers/columnVisibility/columnVisibility';

const CLUB_MESSAGES_COLUMNS_VIEW_KEY = 'clubs.clubInvitations';

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Student search',
    field: 'studentSearch',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: ['playerDetailsData', 'firstName']
  },
  {
    text: 'Name',
    field: 'firstName',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: ['playerDetailsData', 'firstName']
  },
  {
    text: 'Surname',
    field: 'lastName',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: ['playerDetailsData', 'lastName']
  },
  {
    text: 'Updated',
    field: 'updatedAt',
    isSort: true,
    type: FILTER_TYPE.DATE_INTERVAL,
    accessor: getUpdatedAt
  },
  {
    text: 'Responded by',
    field: 'respondedBy',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: message => getResponseUserText(message) || '—'
  },
  {
    text: 'Response',
    field: 'responseActivity',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: message => {
      const respondedAt = getRespondedAtText(message);
      if (!respondedAt) return '—';
      return wasEditedAfterResponse(message) || statusChangedAfterFirstResponse(message)
        ? `${respondedAt} · Updated`
        : respondedAt;
    }
  },
  {
    text: 'Invitation status',
    field: 'invitationStatus',
    isSort: true,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getClubMessageStatus
  },
  {
    text: 'Priority',
    field: 'priority',
    isSort: true,
    accessor: ['priority'],
    type: FILTER_TYPE.SELECT
  },
  {
    text: 'Deadline',
    field: 'deadlineForAnswers',
    isSort: true,
    accessor: getClubMessageDeadline,
    type: FILTER_TYPE.NONE
  },
  {
    text: 'Participant',
    field: 'participant',
    isSort: false,
    type: FILTER_TYPE.SELECT,
    accessor: getIsParticipant
  }
];

interface ErrorDetail {
  name: string;
  maxParticipants: number;
  participantsCount: number;
  potentialClubParticipantsCount: number;
}

interface State {
  items: ClubMessage[];
  currentPage: number;
  selectedItems: ClubMessage[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  isDataFiltered: boolean;
  filters: any;
  isLoading: boolean;
  isSelectAllChecked: boolean;
  total: number;
  itemsWithWaitingListCanBookStatusCount: number;
  basePath: string;
  deadlineForAnswers: string;
  additionalOptions: {
    isBookingFromWaitingList: boolean;
  };

  club: Club;
  availableParticipants: number;

  isInviteToBookModalOpen: boolean;
  isClubErrorModalOpen: boolean;
  isClubInfoModalOpen: boolean;

  currentTabIndex: number;
  errors: ErrorDetail[];
  previewItem?: ClubMessage;
  isColumnsMenuOpen: boolean;
  hiddenColumnKeys: string[];
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

export class ClubMessages extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      items: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      isDataFiltered: false,
      filters: {},
      isLoading: false,
      isSelectAllChecked: false,
      total: 0,
      itemsWithWaitingListCanBookStatusCount: 0,
      basePath: '',
      deadlineForAnswers: '',
      additionalOptions: {
        isBookingFromWaitingList: true
      },

      club: undefined,
      availableParticipants: 0,

      isInviteToBookModalOpen: false,
      isClubErrorModalOpen: false,
      isClubInfoModalOpen: false,

      currentTabIndex: 0,
      errors: [],
      previewItem: undefined,
      isColumnsMenuOpen: false,
      hiddenColumnKeys: loadHiddenColumns(props.user, CLUB_MESSAGES_COLUMNS_VIEW_KEY)
    };
  }

  componentDidMount() {
    this.setState({
      isLoading: true
    });

    this.setAdditionalItems().then(res => {
      this.setItems();
    });
    document.addEventListener('mousedown', this.onDocumentMouseDown);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.onDocumentMouseDown);
    window.dispatchEvent(new CustomEvent('sit:topbar-breadcrumb-clear'));
  }

  onDocumentMouseDown = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;
    if (target?.closest?.('.clubsDsColumns')) return;
    if (this.state.isColumnsMenuOpen) this.setState({ isColumnsMenuOpen: false });
  };

  setHiddenColumns = (hiddenColumnKeys: string[]): void => {
    saveHiddenColumns(this.props.user, CLUB_MESSAGES_COLUMNS_VIEW_KEY, hiddenColumnKeys);
    this.setState({ hiddenColumnKeys });
  };

  toggleColumn = (key: string): void => {
    const hiddenColumnKeys = this.state.hiddenColumnKeys.includes(key)
      ? this.state.hiddenColumnKeys.filter(field => field !== key)
      : [...this.state.hiddenColumnKeys, key];
    this.setHiddenColumns(hiddenColumnKeys);
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setState({
        isLoading: true
      });
      this.setItems();
    }
  }

  getClubId(): string {
    const { history } = this.props;
    const search = parse(history.location.search);
    const clubId = search.club;
    return clubId;
  }

  setAdditionalItems() {
    const { user } = this.props;
    const { additionalOptions } = this.state;
    const clubId = this.getClubId();

    const promises = [getClub(user, clubId), getAvailableClubSpacesMax(user, clubId)];

    return BPromise.all(promises).then(([club, availableParticipantsObj]) => {
      const isBookingFromWaitingList = propz.get(club, ['isBookingFromWaitingList'], true);

      const additionalOptionsUpdated = {
        ...additionalOptions,
        isBookingFromWaitingList
      };

      this.setState({
        club,
        availableParticipants: availableParticipantsObj.availableParticipants,
        additionalOptions: additionalOptionsUpdated
      });
      window.dispatchEvent(new CustomEvent('sit:topbar-breadcrumb', { detail: `Clubs / ${club.name} / Invitations` }));

      return true;
    });
  }

  setItems() {
    const { history, user } = this.props;

    const search = parse(history.location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':');
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';

    const filters = getFilters2(COLUMNS, search);

    const isDataFiltered = isFilterExist2(filters);

    const where = getServerFieldSectionWhere(filters);

    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : undefined;
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    const clubId = this.getClubId();

    const whereStatusWaitingListCanBook = {
      invitationStatus: {
        $in: MESSAGE_INVITATION_STATUS.WAITING_LIST_CAN_BOOK
      }
    };

    const getItemsPromise = getClubMessages(user, clubId, serverQueryFilter);
    const getItemsCountPromise = getClubMessagesCount(user, clubId, where);
    const getItemsWithWaitingListCanBookStatusCountPromise = getClubMessagesCount(
      user,
      clubId,
      whereStatusWaitingListCanBook
    );

    const promises = [getItemsPromise, getItemsCountPromise, getItemsWithWaitingListCanBookStatusCountPromise];

    return BPromise.all(promises).then(([items, countObj, waitingListCanBookStatusCountObj]) => {
      this.setState({
        currentPage: page,
        sortDirection: sortDirection,
        sortColumnsName: sortColumnsName,
        isShowFilter: false,
        isDataFiltered: isDataFiltered,
        filters: filters,
        items: items,
        total: countObj.count,
        itemsWithWaitingListCanBookStatusCount: waitingListCanBookStatusCountObj.count,
        basePath: history.location.pathname,
        isLoading: false
      });

      return true;
    });
  }

  setCurrentPageParams = (currentPage: number): void => {
    let search: string[] = [];

    const { filters, sortColumnsName, sortDirection } = this.state;

    if (currentPage !== 1) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    const clubId = this.getClubId();

    this.props.history.push({
      pathname: this.state.basePath,
      search: `club=${clubId}&${search.join('&')}`
    });
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];

    const selectedItemIndex = selectedItems.findIndex(item => selectedItem.id === item.id);
    let selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({
      selectedItems: selectedItemsUpdated
    });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;

    const itemsFiltered = items.filter(item => {
      return selectedItems.every(selectedItem => selectedItem.id !== item.id);
    });

    const nextSelectedItems = [...selectedItems, ...itemsFiltered];

    this.setState({
      selectedItems: nextSelectedItems,
      isSelectAllChecked: true
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;

    this.setState({
      isLoading: true
    });

    const user = this.props.user;

    const where = getServerFieldSectionWhere(filters);

    const clubId = this.getClubId();

    getAllClubMessages(user, clubId, where).then(messages => {
      const messagesFiltered = messages.filter(message => {
        return selectedItems.every(selectedMessage => selectedMessage.id !== message.id);
      });

      const selectedMessages = [...selectedItems, ...messagesFiltered];

      this.setState({
        selectedItems: selectedMessages,
        isSelectAllChecked: true,
        isLoading: false
      });
    });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;

    const order = getOrder(sortField, sortDirection, sortColumnsName);

    let search: string[] = [];
    search.push(`order=${sortField}:${order}`);

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    const clubId = this.getClubId();

    this.props.history.push({
      pathname: this.state.basePath,
      search: `club=${clubId}&${search.join('&')}`
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = COLUMNS.find(col => col.field === filterField);
      const filterType = filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }
        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({
      filters: nextFilters
    });
  };

  onApplyFilterClick = (): void => {
    const { location } = this.props;
    const { state } = location;
    const { filters, sortDirection, sortColumnsName } = this.state;
    let search: string[] = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    const clubId = this.getClubId();

    this.props.history.push({
      pathname: this.state.basePath,
      search: `club=${clubId}&${search.join('&')}`,
      state
    });

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      isShowFilter: false
    });
  };

  onClearFilterClick = (): void => {
    const { location } = this.props;
    const { state } = location;

    const clubId = this.getClubId();

    this.setState({
      filters: getFilters2(COLUMNS, {}),
      selectedItems: [],
      isSelectAllChecked: false,
      isDataFiltered: false,
      isShowFilter: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: `club=${clubId}`,
      state
    });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();

    const isShowFilter = this.state.isShowFilter;

    if (isShowFilter) {
      const appliedFilters = getFilters2(COLUMNS, parse(this.props.history.location.search));
      this.setState({
        isShowFilter: false,
        filters: appliedFilters
      });
      return;
    }

    this.setState({
      isShowFilter: true
    });
  };

  renderClubErrorsModal = () => {
    const { isClubErrorModalOpen, errors } = this.state;
    const errorMessages = errors
      .map(error => {
        return `Club name: ${error.name}, Spaces available: ${error.maxParticipants -
          error.participantsCount}, Students you are trying to add: ${error.potentialClubParticipantsCount}`;
      })
      .join('\n');

    return (
      <SimpleModal
        isOpen={isClubErrorModalOpen}
        title="Add students to club"
        body={`There are no sufficient spaces in the club(s): \n\n${errorMessages}\nPlease either amend the number of spaces available on the club settings page or deselect some students to match the number of available spaces.`}
        onCloseClick={this.clubErrorModalClose}
      />
    );
  };

  clubErrorModalClose = () => {
    this.setState({
      isClubErrorModalOpen: false
    });
  };

  addStudentsToClub = () => {
    const { selectedItems, club } = this.state;
    const { user } = this.props;
    const clubId = this.getClubId();

    this.setState({ isLoading: true });

    const participantsData = selectedItems.map(item => ({
      userId: item.playerDetails.userId,
      permissionId: item.playerDetails.permissionId
    }));

    addClubStudents(user, clubId, { participants: participantsData })
      .then(() => getClub(user, clubId))
      .then(updatedClub => {
        this.setState({
          club: updatedClub,
          isLoading: false,
          selectedItems: []
        });
        this.setItems();
      })
      .catch(error => {
        console.error('Failed to add participants', error);
        this.setState({ isLoading: false });
      });
  };

  clubInfoModalClose = () => {
    this.setState({
      isClubInfoModalOpen: false
    });
  };

  renderClubInfoModal(): React.ReactNode {
    const { selectedItems, isClubInfoModalOpen, club } = this.state;

    if (typeof club === 'undefined') {
      return null;
    }

    const numberPotentialClubParticipants = selectedItems.length;

    const { maxParticipants, participants, name } = club;
    const numberParticipants = participants.length;

    const infoText = `Club name: ${name} \nSpaces available: ${maxParticipants -
      numberParticipants} \nStudents you are trying to add: ${numberPotentialClubParticipants} \n`;

    return (
      <SimpleModal
        isOpen={isClubInfoModalOpen}
        title={'Add students to club'}
        body={infoText}
        buttonText={'Add'}
        onButtonClick={this.addStudentsToClub}
        onCloseClick={this.clubInfoModalClose}
      />
    );
  }

  onAddToClubClick = () => {
    const { selectedItems, club } = this.state;
    const { user } = this.props;
    const clubId = this.getClubId();
    if (club.maxParticipants !== 0 && club.participants.length + selectedItems.length > club.maxParticipants) {
      const errors = [
        {
          name: club.name,
          maxParticipants: club.maxParticipants,
          participantsCount: club.participants.length,
          potentialClubParticipantsCount: selectedItems.length
        }
      ];
      this.setState({
        isClubErrorModalOpen: true,
        errors: errors
      });
    } else {
      this.setState({ isLoading: true });
      const participantsData = selectedItems.map(item => ({
        userId: item.playerDetails.userId,
        permissionId: item.playerDetails.permissionId
      }));

      addClubStudents(user, clubId, { participants: participantsData })
        .then(() => getClub(user, clubId))
        .then(updatedClub => {
          return this.setItems().then(() => {
            this.setState({
              club: updatedClub,
              selectedItems: [],
              isLoading: false
            });
          });
        })
        .catch(error => {
          console.error('Failed to add participants', error);
          this.setState({ isLoading: false });
        });
    }
  };

  isAddToClubButtonActive(): boolean {
    const { selectedItems, club } = this.state;
    const isActiveClub = typeof club !== 'undefined' && club.status !== 'ACTIVE';
    const isSomeSelectedItemsParticipant = selectedItems.some(message => message.isParticipant);
    return selectedItems.length !== 0 && !isSomeSelectedItemsParticipant && isActiveClub;
  }

  renderInviteToBookModal(): React.ReactNode {
    const { isInviteToBookModalOpen, deadlineForAnswers } = this.state;

    return (
      <SimpleModal
        isOpen={isInviteToBookModalOpen}
        title={'Invite to book'}
        body={''}
        buttonText={'invite'}
        onButtonClick={this.onInviteToBookClick}
        onCloseClick={this.closeInviteToBookModal}
      >
        <div className="mt-4 mb-2">Deadline for answers (optional)</div>
        <MaskedInput
          type="text"
          className={'form-control'}
          value={deadlineForAnswers}
          onChange={this.onDeadlineChange}
          mask={UK_INPUT_MASK}
          placeholder={UK_INPUT_MASK_VALIDATION}
        />
      </SimpleModal>
    );
  }

  openInviteToBookModal = () => {
    this.setState({
      isInviteToBookModalOpen: true
    });
  };

  closeInviteToBookModal = () => {
    this.setState({
      isInviteToBookModalOpen: false,
      deadlineForAnswers: ''
    });
  };

  onDeadlineChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    this.setState({
      deadlineForAnswers: value
    });
  };

  onInviteToBookClick = () => {
    const { user } = this.props;
    const { selectedItems, deadlineForAnswers } = this.state;
    const isDeadlineExist = deadlineForAnswers !== '';
    const clubId = this.getClubId();
    const messageId = selectedItems[0].id;

    const isSelectedItemInvitationStatusWaitingList =
      propz.get(selectedItems[0], ['invitationStatus']) === CLUB_PARTICIPATION_MESSAGE_INVITATION_STATUS.WAITING_LIST;

    const isSelectedItemsInvitationStatusOutdated = selectedItems.every(item => {
      return item.invitationStatus === CLUB_PARTICIPATION_MESSAGE_INVITATION_STATUS.OUTDATED;
    });

    this.setState({
      isLoading: true
    });

    switch (true) {
      case isSelectedItemInvitationStatusWaitingList:
        let data;

        if (isDeadlineExist) {
          const deadlineForAnswersDate = moment(deadlineForAnswers, UK_INPUT_MASK_VALIDATION, true).toDate();
          const deadlineForAnswersString = deadlineForAnswersDate.toISOString();

          data = {
            invitationStatus: MESSAGE_INVITATION_STATUS.WAITING_LIST_CAN_BOOK,
            deadlineForAnswers: deadlineForAnswersString
          };
        } else {
          data = {
            invitationStatus: MESSAGE_INVITATION_STATUS.WAITING_LIST_CAN_BOOK
          };
        }

        updateClubMessage(user, clubId, messageId, data)
          .then(() => {
            return this.setItems();
          })
          .then(() => {
            this.setState({
              isInviteToBookModalOpen: false,
              selectedItems: []
            });
          });

        break;

      case isSelectedItemsInvitationStatusOutdated:
        const messageIdList = selectedItems.map(item => item.id);

        cancelClubMessageOutdatedStatus(user, clubId, messageIdList)
          .then(() => {
            return this.setItems();
          })
          .then(() => {
            this.setState({
              selectedItems: []
            });
          });

        break;
    }
  };

  onIsBookingFromWaitingListChange = (event, field) => {
    const { user } = this.props;
    const clubId = this.getClubId();

    this.setState({
      isLoading: true
    });

    const { additionalOptions } = this.state;
    const value = event.target.checked;

    const additionalOptionsUpdated = {
      ...additionalOptions,
      [field]: value
    };

    updateClubBookingType(user, clubId, value)
      .then(() => {
        return this.setItems();
      })
      .then(() => {
        this.setState({
          additionalOptions: additionalOptionsUpdated
        });
      });
  };

  renderAdditionalOptions = () => {
    const { additionalOptions, club, itemsWithWaitingListCanBookStatusCount, availableParticipants } = this.state;
    const { isBookingFromWaitingList } = additionalOptions;
    const maxParticipants = propz.get(club, ['maxParticipants']);
    const spacesLeft = maxParticipants === 0 ? 'Unlimited' : availableParticipants;
    const isPaidClub = propz.get(club, ['price', 'priceType'], CLUB_PRICING.FREE) !== CLUB_PRICING.FREE;

    return (
      <div className="clubMessagesDsOptionsLine">
        {isPaidClub && (
          <Switch
            value={isBookingFromWaitingList}
            onChange={event => this.onIsBookingFromWaitingListChange(event, 'isBookingFromWaitingList')}
            text="Book from waiting list"
            name="isBookingFromWaitingList"
          />
        )}
        <span>{spacesLeft} spaces left</span>
        {isPaidClub && <span>{itemsWithWaitingListCanBookStatusCount} invited from waiting list</span>}
      </div>
    );
  };

  goBack = () => {
    const { history, location } = this.props;
    const search = propz.get(location, ['state', 'search']);
    const isSearchExist = typeof search !== 'undefined';

    const clubId = this.getClubId();

    history.push({
      pathname: '/clubs',
      search: isSearchExist ? search : '',
      state: {
        ...search,
        clubId
      }
    });
  };

  renderGrid(): React.ReactNode {
    const { user } = this.props;
    const {
      items,
      additionalOptions,
      sortDirection,
      sortColumnsName,
      isShowFilter,
      isDataFiltered,
      selectedItems,
      filters,
      isSelectAllChecked,
      currentPage,
      total,
      itemsWithWaitingListCanBookStatusCount,
      club,
      availableParticipants
    } = this.state;

    const { isBookingFromWaitingList } = additionalOptions;

    const clubStatus = propz.get(club, ['status'], '');
    const isClubStatusActive = clubStatus === CLUB_STATUS.ACTIVE;

    const firstSelectedInvitationStatus = propz.get(selectedItems[0], ['invitationStatus']);

    const isSelectedItemInvitationStatusWaitingList =
      firstSelectedInvitationStatus === CLUB_PARTICIPATION_MESSAGE_INVITATION_STATUS.WAITING_LIST;

    const isSelectedItemsInvitationStatusOutdated = selectedItems.every(item => {
      return item.invitationStatus === CLUB_PARTICIPATION_MESSAGE_INVITATION_STATUS.OUTDATED;
    });

    const isInviteToBookForWaitingListStatusActive =
      itemsWithWaitingListCanBookStatusCount < availableParticipants &&
      selectedItems.length === 1 &&
      isSelectedItemInvitationStatusWaitingList &&
      isBookingFromWaitingList;

    const isInviteToBookForOutdatedStatusActive =
      isClubStatusActive &&
      !isBookingFromWaitingList &&
      selectedItems.length > 0 &&
      isSelectedItemsInvitationStatusOutdated;

    const filterOptions = {
      invitationStatus: getSelectOptionsForClubMessagesStatus(user),
      priority: getSelectOptionsForClubPriority(),
      participant: getSelectOptionForBooleanQuestion()
    };
    const tableColumns = [
      { key: 'student', label: 'Student', locked: true },
      { key: 'updated', label: 'Updated' },
      { key: 'respondedBy', label: 'Responded by' },
      { key: 'response', label: 'Response' },
      { key: 'status', label: 'Status' },
      { key: 'priority', label: 'Priority' },
      { key: 'deadline', label: 'Deadline' },
      { key: 'participant', label: 'Participant' }
    ];
    const visible = (key: string) => !this.state.hiddenColumnKeys.includes(key);
    const visibleColumnCount = tableColumns.filter(column => column.locked || visible(column.key)).length;
    const statusValues = Array.isArray(filters.invitationStatus)
      ? filters.invitationStatus
      : filters.invitationStatus
      ? [filters.invitationStatus]
      : [];
    const pageCount = Math.max(1, getPagesCount(total, LIMIT));
    const pageWindowStart = Math.max(1, Math.min(currentPage - 2, pageCount - 4));
    const pageWindowEnd = Math.min(pageCount, pageWindowStart + 4);
    const pages = Array.from({ length: pageWindowEnd - pageWindowStart + 1 }, (_, index) => pageWindowStart + index);
    const allOnPageSelected = items.length > 0 && items.every(item => selectedItems.some(row => row.id === item.id));
    const isPaidClub = propz.get(club, ['price', 'priceType'], CLUB_PRICING.FREE) !== CLUB_PRICING.FREE;
    const formatDate = (value: any, withTime = false) => {
      const date = value ? moment(value) : null;
      if (!date || !date.isValid()) return '—';
      return date.format(withTime ? 'DD MMM YYYY, HH:mm' : 'DD MMM YYYY');
    };
    const getStatusClass = (message: ClubMessage) => {
      const status = String(message.invitationStatus || '').toUpperCase();
      if (status === 'ACCEPTED') return 'isSuccess';
      if (status === 'REJECTED' || status === 'OUTDATED') return 'isDanger';
      if (status.indexOf('WAITING_LIST') !== -1) return 'isWarning';
      return 'isNeutral';
    };
    const setStatusFilter = (value: string) => {
      const next = statusValues.map(String).includes(value)
        ? statusValues.filter(item => String(item) !== value)
        : [...statusValues, value];
      this.setState({ filters: { ...filters, invitationStatus: next } });
    };
    const appliedFilters = getFilters2(COLUMNS, parse(this.props.history.location.search));
    const appliedStatusValues = Array.isArray(appliedFilters.invitationStatus)
      ? appliedFilters.invitationStatus
      : appliedFilters.invitationStatus
      ? [appliedFilters.invitationStatus]
      : [];
    const getFilterOptionLabel = (field: 'invitationStatus' | 'priority' | 'participant', value: any) => {
      const option = filterOptions[field].find(candidate => String(candidate.value) === String(value));
      return option ? String(option.text) : String(value);
    };
    const activeFilterChips = [
      appliedFilters.studentSearch
        ? { key: 'studentSearch', label: 'Search', value: String(appliedFilters.studentSearch) }
        : undefined,
      appliedStatusValues.length > 0
        ? {
            key: 'invitationStatus',
            label: 'Status',
            value: appliedStatusValues.map(value => getFilterOptionLabel('invitationStatus', value)).join(', ')
          }
        : undefined,
      appliedFilters.priority
        ? {
            key: 'priority',
            label: 'Priority',
            value: getFilterOptionLabel('priority', appliedFilters.priority)
          }
        : undefined,
      appliedFilters.participant !== '' && typeof appliedFilters.participant !== 'undefined'
        ? {
            key: 'participant',
            label: 'Participant',
            value: getFilterOptionLabel('participant', appliedFilters.participant)
          }
        : undefined
    ].filter(Boolean) as Array<{ key: string; label: string; value: string }>;
    const clearIndividualFilter = (field: string) => {
      this.setState(
        state => ({
          filters: {
            ...state.filters,
            [field]: field === 'invitationStatus' ? [] : ''
          },
          selectedItems: [],
          isSelectAllChecked: false
        }),
        this.onApplyFilterClick
      );
    };

    return (
      <div className="clubsDsPage clubsInvitationsDsPage clubMessagesDsPage">
        <main className="clubsDsMain">
          <div className="clubsDsHeader clubManagementHeader">
            <div className="clubManagementHeader__context">
              <button type="button" className="clubManagementHeader__back" onClick={this.goBack}>
                ← Back to clubs
              </button>
              <span className="clubManagementHeader__divider" aria-hidden="true" />
              <div className="clubManagementHeader__copy">
                <h1>Club bookings</h1>
                <p>{propz.get(club, ['name'], 'Club')}</p>
              </div>
            </div>
            <div className="clubManagementHeader__stats" aria-label="Booking summary">
              <span>
                Invitations <strong>{total}</strong>
              </span>
              <span>
                Participants <strong>{propz.get(club, ['participants', 'length'], 0)}</strong>
              </span>
              <span>
                Spaces left <strong>{propz.get(club, ['maxParticipants'], 0) === 0 ? '∞' : availableParticipants}</strong>
              </span>
              {isPaidClub && (
                <span className="isWaiting">
                  Waiting list ready <strong>{itemsWithWaitingListCanBookStatusCount}</strong>
                </span>
              )}
            </div>
          </div>

          <section className="clubManagementContentCard">
          <div className="clubsDsToolbar clubMessagesDsToolbar">
            <div className="clubsDsToolbarTitle">
              <strong>Booking responses</strong>
              <span className="clubsDsCountChip">{total}</span>
            </div>
            <div className="clubsDsSearch clubMessagesDsSearch">
              <input
                value={filters.studentSearch || ''}
                placeholder="Search student"
                onChange={event => this.onTableFilterChange(event, 'studentSearch')}
                onKeyDown={event => event.key === 'Enter' && this.onApplyFilterClick()}
              />
            </div>
            <button type="button" className="clubsDsBtn clubsDsBtnSecondary" onClick={this.onTableFilterClick}>
              <svg
                viewBox="0 0 24 24"
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.9"
                aria-hidden="true"
              >
                <path d="M4 6h16M7 12h10M10 18h4" />
              </svg>
              Filters
            </button>
            <div className="clubsDsColumns">
              <button
                type="button"
                className="clubsDsBtn clubsDsBtnSecondary"
                onClick={() => this.setState({ isColumnsMenuOpen: !this.state.isColumnsMenuOpen })}
              >
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.9"
                  aria-hidden="true"
                >
                  <path d="M4 5h16v14H4zM9 5v14M15 5v14" />
                </svg>
                Columns <span className="clubsDsBtnBadge">{visibleColumnCount}</span>
              </button>
              {this.state.isColumnsMenuOpen && (
                <div className="clubsDsColumnsMenu">
                  <div className="clubsDsColumnsHead">
                    <span>Show / hide columns</span>
                    <button type="button" onClick={() => this.setHiddenColumns([])}>
                      Reset
                    </button>
                  </div>
                  {tableColumns.map(column => (
                    <label key={column.key} className={`clubsDsColumnsItem ${column.locked ? 'isLocked' : ''}`}>
                      <input
                        type="checkbox"
                        checked={column.locked || visible(column.key)}
                        disabled={column.locked}
                        onChange={() => this.toggleColumn(column.key)}
                      />
                      <span>{column.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {activeFilterChips.length > 0 && (
            <div className="clubMessagesDsActiveFilters" aria-label="Applied filters">
              {activeFilterChips.map(chip => (
                <span key={chip.key}>
                  <b>{chip.label}:</b> {chip.value}
                  <button
                    type="button"
                    onClick={() => clearIndividualFilter(chip.key)}
                    aria-label={`Remove ${chip.label.toLowerCase()} filter`}
                  >
                    ×
                  </button>
                </span>
              ))}
              <button type="button" className="clubMessagesDsClearFilters" onClick={this.onClearFilterClick}>
                Clear all
              </button>
            </div>
          )}

          {isShowFilter && (
            <>
              <div className="clubsInvitationsFilterScrim" onClick={this.onTableFilterClick} aria-hidden="true" />
              <aside className="clubsInvitationsFilters" aria-label="Invitation filters">
                <header>
                  <div>
                    <strong>Filters</strong>
                  </div>
                  <button type="button" onClick={this.onTableFilterClick} aria-label="Close filters">
                    ×
                  </button>
                </header>
                <div className="clubsDsFilterBody">
                  <div className="clubsDsFilterField">
                    <span>Invitation status</span>
                    <div className="clubsDsFilterChips">
                      {filterOptions.invitationStatus
                        .filter(option => option.value)
                        .filter(option => isPaidClub || String(option.value).indexOf('WAITING_LIST') === -1)
                        .map(option => (
                          <button
                            type="button"
                            key={String(option.value)}
                            className={`clubsDsFilterChip ${
                              statusValues.map(String).includes(String(option.value)) ? 'isSelected' : ''
                            }`}
                            onClick={() => setStatusFilter(String(option.value))}
                          >
                            {option.text}
                          </button>
                        ))}
                    </div>
                  </div>
                  <label className="clubsDsFilterField">
                    <span>Priority</span>
                    <select
                      value={filters.priority || ''}
                      onChange={event => this.onTableFilterChange(event, 'priority')}
                    >
                      <option value="">All priorities</option>
                      {filterOptions.priority
                        .filter(option => option.value !== '')
                        .map(option => (
                          <option key={String(option.value)} value={String(option.value)}>
                            {option.text}
                          </option>
                        ))}
                    </select>
                  </label>
                  <div className="clubsDsFilterField">
                    <span>Participant</span>
                    <div className="clubsDsFilterChips">
                      {filterOptions.participant
                        .filter(option => option.value !== '')
                        .map(option => (
                          <button
                            type="button"
                            key={String(option.value)}
                            className={`clubsDsFilterChip ${
                              String(filters.participant || '') === String(option.value) ? 'isSelected' : ''
                            }`}
                            onClick={() =>
                              this.onTableFilterChange(
                                {
                                  target: {
                                    value:
                                      String(filters.participant || '') === String(option.value) ? '' : option.value
                                  }
                                },
                                'participant'
                              )
                            }
                          >
                            {option.text}
                          </button>
                        ))}
                    </div>
                  </div>
                </div>
                <div className="clubsDsFilterActions">
                  <button
                    type="button"
                    className="clubsDsBtn clubsDsBtnGhost is-clear"
                    onClick={this.onClearFilterClick}
                  >
                    Clear all
                  </button>
                  <div>
                    <button
                      type="button"
                      className="clubsDsBtn clubsDsBtnGhost"
                      onClick={this.onTableFilterClick}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="clubsDsBtn clubsDsBtnPrimary is-apply"
                      onClick={this.onApplyFilterClick}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </aside>
            </>
          )}

          <div className="clubMessagesDsOptions">{this.renderAdditionalOptions()}</div>

          {selectedItems.length > 0 && (
            <div className="clubsDsSelectionBar">
              <div className="clubsDsSelectionCount">{selectedItems.length} selected</div>
              {this.isAddToClubButtonActive() && (
                <button type="button" className="clubsDsBtn clubsDsBtnSecondary" onClick={this.onAddToClubClick}>
                  Add to club
                </button>
              )}
              {(isInviteToBookForWaitingListStatusActive || isInviteToBookForOutdatedStatusActive) && (
                <button type="button" className="clubsDsBtn clubsDsBtnSecondary" onClick={this.onInviteToBookClick}>
                  Invite to book
                </button>
              )}
              {isInviteToBookForWaitingListStatusActive && (
                <button type="button" className="clubsDsBtn clubsDsBtnSecondary" onClick={this.openInviteToBookModal}>
                  Invite with deadline
                </button>
              )}
              <button type="button" className="clubsDsBtn clubsDsBtnGhost" onClick={this.onUnselectAllClick}>
                Clear
              </button>
            </div>
          )}

          <div className="clubsDsTableCard">
            <div className="clubsDsTableScroll">
              <table className="clubsDsTable">
                <thead>
                  <tr>
                    <th className="clubsDsCheckCell">
                      <SelectAllCheckbox
                        compact
                        isChecked={allOnPageSelected || isSelectAllChecked}
                        totalCount={total}
                        currentPage={currentPage}
                        pageItemsCount={items.length}
                        selectedItems={selectedItems}
                        onSelectAllOnPageClick={this.onSelectAllOnPageClick}
                        onSelectAllClick={this.onSelectAllClick}
                        onUnselectAllClick={this.onUnselectAllClick}
                      />
                    </th>
                    <th>Student</th>
                    {visible('updated') && <th>Updated</th>}
                    {visible('respondedBy') && <th>Responded by</th>}
                    {visible('response') && <th>Response</th>}
                    {visible('status') && (
                      <th>
                        <button
                          type="button"
                          className="clubsDsSort"
                          onClick={() => this.onTableSortClick('invitationStatus')}
                        >
                          Status {sortColumnsName === 'invitationStatus' ? (sortDirection === 'ASC' ? '↑' : '↓') : ''}
                        </button>
                      </th>
                    )}
                    {visible('priority') && <th>Priority</th>}
                    {visible('deadline') && <th>Deadline</th>}
                    {visible('participant') && <th>Participant</th>}
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={visibleColumnCount + 1} className="clubsDsEmpty">
                        No invitations match these filters.
                      </td>
                    </tr>
                  ) : (
                    items.map((message, index) => {
                      const checked = selectedItems.some(item => item.id === message.id);
                      const student = `${message.playerDetailsData.firstName || ''} ${message.playerDetailsData
                        .lastName || ''}`.trim();
                      const respondedAt = getRespondedAtText(message);
                      const changed = wasEditedAfterResponse(message) || statusChangedAfterFirstResponse(message);
                      return (
                        <tr
                          key={message.id}
                          className={this.state.previewItem?.id === message.id ? 'isPreview' : ''}
                          onClick={() => this.setState({ previewItem: message })}
                        >
                          <td className="clubsDsCheckCell" onClick={event => event.stopPropagation()}>
                            <input type="checkbox" checked={checked} onChange={() => this.onItemClick(index)} />
                          </td>
                          <td>
                            <div className="clubsInvitationsStudent">
                              <span>
                                {student
                                  ? student
                                      .split(/\s+/)
                                      .map(part => part.charAt(0))
                                      .slice(0, 2)
                                      .join('')
                                  : '?'}
                              </span>
                              <strong>{student || '—'}</strong>
                            </div>
                          </td>
                          {visible('updated') && <td>{formatDate(message.updatedAt, true)}</td>}
                          {visible('respondedBy') && (
                            <td className="clubMessagesDsMultiline">{getResponseUserText(message) || '—'}</td>
                          )}
                          {visible('response') && (
                            <td>
                              {respondedAt ? (
                                <>
                                  {respondedAt}
                                  {changed && <span className="clubsInvitationDrawer__updated">Updated</span>}
                                </>
                              ) : (
                                '—'
                              )}
                            </td>
                          )}
                          {visible('status') && (
                            <td>
                              <span className={`clubsDsBadge ${getStatusClass(message)}`}>
                                {getClubMessageStatus(message)}
                              </span>
                            </td>
                          )}
                          {visible('priority') && <td>{message.priority || '—'}</td>}
                          {visible('deadline') && <td>{formatDate((message as any).deadlineForAnswers)}</td>}
                          {visible('participant') && <td>{getIsParticipant(message)}</td>}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="clubsDsFooter">
            <span className="clubsDsPaginationSummary">
              Page {currentPage} of {pageCount}
            </span>
            <button
              type="button"
              className="clubsDsPagerBtn"
              disabled={currentPage <= 1}
              onClick={() => this.setCurrentPageParams(currentPage - 1)}
            >
              Previous
            </button>
            {pageWindowStart > 1 && (
              <>
                <button type="button" className="clubsDsPageBtn" onClick={() => this.setCurrentPageParams(1)}>
                  1
                </button>
                {pageWindowStart > 2 && <span className="clubsDsPaginationEllipsis">...</span>}
              </>
            )}
            {pages.map(page => (
              <button
                type="button"
                key={page}
                className={`clubsDsPageBtn ${page === currentPage ? 'isActive' : ''}`}
                onClick={() => this.setCurrentPageParams(page)}
              >
                {page}
              </button>
            ))}
            {pageWindowEnd < pageCount && (
              <>
                {pageWindowEnd < pageCount - 1 && <span className="clubsDsPaginationEllipsis">...</span>}
                <button type="button" className="clubsDsPageBtn" onClick={() => this.setCurrentPageParams(pageCount)}>
                  {pageCount}
                </button>
              </>
            )}
            <button
              type="button"
              className="clubsDsPagerBtn"
              disabled={currentPage >= pageCount}
              onClick={() => this.setCurrentPageParams(currentPage + 1)}
            >
              Next
            </button>
          </div>
          </section>
        </main>
      </div>
    );
  }

  onTabClick = (event, index) => {
    event.preventDefault();

    this.setState({ currentTabIndex: index });
  };

  getSummaryTabs(): React.ReactNode {
    const { currentTabIndex, selectedItems } = this.state;

    const message = selectedItems[0];

    switch (currentTabIndex) {
      case 0:
        return <ClubConfirmationRequestSummary message={message} />;
    }
  }

  renderSummary(): React.ReactNode {
    const { currentTabIndex, selectedItems } = this.state;
    const isOneItemSelected = this.isOneItemSelected();

    if (!isOneItemSelected) {
      return <div className="font-weight-bold">Select an invitation from the list above</div>;
    }

    const { invitationStatus } = selectedItems[0];
    const isConfirmationRequestRejected = invitationStatus === CONFIRMATION_REQUESTS_STATUS.REJECTED;
    const isConfirmationRequestNotReady = invitationStatus === CONFIRMATION_REQUESTS_STATUS.NOT_READY;

    if (isConfirmationRequestRejected || isConfirmationRequestNotReady) {
      return null;
    }

    const summaryTabs = this.getSummaryTabs();

    return (
      <GenericSummary
        onTabClick={this.onTabClick}
        tabs={CLUB_CONFIRMATION_REQUEST_TABS}
        currentTabIndex={currentTabIndex}
      >
        {summaryTabs}
      </GenericSummary>
    );
  }

  isOneItemSelected(): boolean {
    const selectedItems = this.state.selectedItems;
    return selectedItems.length === 1;
  }

  closeResponseDrawer = (): void => {
    this.setState({ previewItem: undefined, currentTabIndex: 0 });
  };

  renderResponseDrawer(): React.ReactNode {
    const message = this.state.previewItem;
    if (!message) return null;
    const studentName = `${propz.get(message, ['playerDetailsData', 'firstName'], '')} ${propz.get(
      message,
      ['playerDetailsData', 'lastName'],
      ''
    )}`.trim();
    const status = getClubMessageStatus(message);
    const respondedBy = getResponseUserText(message);
    const respondedAt = getRespondedAtText(message);
    const wasUpdated = wasEditedAfterResponse(message) || statusChangedAfterFirstResponse(message);
    const hasResponse = Boolean(respondedAt);
    const initials = studentName
      ? studentName
          .split(/\s+/)
          .map(part => part.charAt(0))
          .slice(0, 2)
          .join('')
      : '?';
    const rawStatus = String(message.invitationStatus || '').toUpperCase();
    const statusClass =
      rawStatus === 'ACCEPTED'
        ? 'isSuccess'
        : rawStatus === 'REJECTED' || rawStatus === 'OUTDATED'
        ? 'isDanger'
        : rawStatus.indexOf('WAITING_LIST') !== -1
        ? 'isWarning'
        : 'isNeutral';
    const formatDateTime = (value?: string) => {
      const date = value ? moment(value) : null;
      return date && date.isValid() ? date.format('DD MMM YYYY, HH:mm') : '—';
    };

    return (
      <>
        <div className="clubsDsDrawerScrim" onClick={this.closeResponseDrawer} aria-hidden="true" />
        <aside
          className="clubsDsDrawer clubsInvitationsDrawer clubBookingResponseDrawer"
          role="dialog"
          aria-modal="true"
        >
          <header className="clubsInvitationDrawer__head">
            <span className="clubsInvitationDrawer__avatar" aria-hidden="true">
              {initials}
            </span>
            <div className="clubsInvitationDrawer__identity">
              <h2>{studentName || 'Booking response'}</h2>
              <div>
                <span>{this.state.club?.name || 'Club invitation'}</span>
                <i />
                <span className={`clubsDsBadge ${statusClass}`}>{status}</span>
                {wasUpdated && <span className="clubsInvitationDrawer__updated">Updated response</span>}
              </div>
            </div>
            <button
              type="button"
              className="clubsInvitationDrawer__close"
              onClick={this.closeResponseDrawer}
              aria-label="Close booking response"
            >
              ×
            </button>
          </header>
          <div className="clubsInvitationDrawer__body">
            <section className="clubsInvitationDrawer__section">
              <h3>Invitation</h3>
              <dl className="clubsInvitationDrawer__details">
                <div>
                  <dt>Club</dt>
                  <dd>{this.state.club?.name || '—'}</dd>
                </div>
                <div>
                  <dt>Schedule</dt>
                  <dd>
                    {getClubDays(message)} at {getClubStartTimeFromMessage(message)}
                  </dd>
                </div>
                <div>
                  <dt>Invitation sent</dt>
                  <dd>{formatDateTime(message.createdAt)}</dd>
                </div>
                <div>
                  <dt>Last updated</dt>
                  <dd>{formatDateTime(message.updatedAt)}</dd>
                </div>
                <div>
                  <dt>Participant</dt>
                  <dd>{getIsParticipant(message)}</dd>
                </div>
                <div>
                  <dt>Priority</dt>
                  <dd>{message.priority || '—'}</dd>
                </div>
              </dl>
            </section>
            <section className="clubsInvitationDrawer__section">
              <div className="clubsInvitationDrawer__sectionHead">
                <div>
                  <h3>Booking response</h3>
                  <p>Response details and submitted form answers.</p>
                </div>
                {wasUpdated && <span className="clubsInvitationDrawer__updated">Updated after response</span>}
              </div>
              {hasResponse ? (
                <>
                  <div className="clubsInvitationDrawer__responseMeta">
                    <div>
                      <span>Responded by</span>
                      <strong>{respondedBy || 'Unknown user'}</strong>
                    </div>
                    <div>
                      <span>Responded at</span>
                      <strong>{respondedAt}</strong>
                    </div>
                  </div>
                  <div className="clubsInvitationDrawer__answers">
                    <ClubConfirmationRequestSummary message={message} />
                  </div>
                </>
              ) : (
                <div className="clubsInvitationDrawer__empty">
                  <span aria-hidden="true">?</span>
                  <strong>No response submitted</strong>
                  <p>This invitation has not been answered yet.</p>
                </div>
              )}
            </section>
          </div>
        </aside>
      </>
    );
  }

  render() {
    const { isLoading, isClubErrorModalOpen, isClubInfoModalOpen } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    const classes = isClubErrorModalOpen || isClubInfoModalOpen ? 'modal-open' : '';

    return (
      <div className={classes}>
        {/*Place for render modal windows*/}
        {this.renderClubErrorsModal()}
        {this.renderClubInfoModal()}
        {this.renderInviteToBookModal()}
        {this.renderGrid()}
        {this.renderResponseDrawer()}
      </div>
    );
  }
}
